import {
    DeleteOutlined,
    DownloadOutlined,
    LockOpenOutlined,
    LockOutlined,
    SendOutlined,
    VisibilityOutlined,
} from '@mui/icons-material';
import {
    Chip,
    CircularProgress,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import React from 'react';

import Result from 'components/v2/common/Result';

import { ACTIONS } from 'constant';
import { getRetainerStatus, getRetainerTypeLabelKey } from 'helpers/applicationRetainers';
import useTranslate from 'hooks/useTranslate';
import type { ApplicationRetainer, ApplicationRetainerStatus } from 'types';
import { isAgreementSigningBlocked } from 'utils/agreement';

export type CaseRetainerAction =
    | ACTIONS.PREVIEW_FILE
    | ACTIONS.DOWNLOAD
    | ACTIONS.SEND
    | ACTIONS.EDIT
    | ACTIONS.DELETE;

interface CaseRetainersTableProps {
    retainers: ApplicationRetainer[];
    pending: { agreementId: number; action: CaseRetainerAction } | null;
    handleAction: (action: CaseRetainerAction, retainer: ApplicationRetainer) => void;
}

const STATUS_LABEL: Record<ApplicationRetainerStatus, string> = {
    draft: 'retainerStatusDraft',
    sent: 'retainerStatusSent',
    signed: 'retainerStatusSigned',
};

const STATUS_COLOR: Record<ApplicationRetainerStatus, 'default' | 'info' | 'success'> = {
    draft: 'default',
    sent: 'info',
    signed: 'success',
};

export const CaseRetainersTable: React.FC<CaseRetainersTableProps> = ({
    retainers,
    pending,
    handleAction,
}) => {
    const { translate } = useTranslate();

    if (retainers.length === 0) {
        return (
            <Result
                title={translate('noRetainersYet')}
                description={translate('noRetainersYetDescription')}
                sx={{ py: 2 }}
            />
        );
    }

    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>{translate('type')}</TableCell>
                        <TableCell>{translate('status')}</TableCell>
                        <TableCell>{translate('signingStatus')}</TableCell>
                        <TableCell align="right">{translate('actions')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {retainers.map((retainer) => {
                        const status = getRetainerStatus(retainer);
                        const signingBlocked = isAgreementSigningBlocked(retainer);
                        const busy = pending?.agreementId === retainer.agreement_id;
                        const pendingAction = busy ? pending.action : null;
                        const sendLabel = translate(
                            status === 'sent' ? 'resendToClient' : 'sendToClient'
                        );
                        const signingActionLabel = translate(
                            signingBlocked ? 'allowRetainerSigning' : 'blockRetainerSigning'
                        );

                        return (
                            <TableRow key={retainer.agreement_id} hover>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {translate(
                                            getRetainerTypeLabelKey(retainer.agreement_type)
                                        )}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {`#${retainer.agreement_id}`}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={translate(STATUS_LABEL[status])}
                                        color={STATUS_COLOR[status]}
                                        variant={status === 'draft' ? 'outlined' : 'filled'}
                                        sx={{ fontWeight: 600 }}
                                    />
                                </TableCell>
                                <TableCell>
                                    {status === 'signed' ? (
                                        <Typography variant="body2" color="text.secondary">
                                            —
                                        </Typography>
                                    ) : (
                                        <Chip
                                            size="small"
                                            icon={
                                                signingBlocked ? (
                                                    <LockOutlined sx={{ fontSize: 16 }} />
                                                ) : (
                                                    <LockOpenOutlined sx={{ fontSize: 16 }} />
                                                )
                                            }
                                            label={translate(
                                                signingBlocked
                                                    ? 'retainerSigningBlocked'
                                                    : 'retainerSigningOpen'
                                            )}
                                            color={signingBlocked ? 'warning' : 'success'}
                                            variant="outlined"
                                            sx={{ fontWeight: 600 }}
                                        />
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    <Stack
                                        direction="row"
                                        spacing={0.25}
                                        sx={{ justifyContent: 'flex-end' }}
                                    >
                                        <Tooltip title={translate('view')}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={busy}
                                                    onClick={() =>
                                                        handleAction(ACTIONS.PREVIEW_FILE, retainer)
                                                    }
                                                    aria-label={translate('view')}
                                                >
                                                    {pendingAction === ACTIONS.PREVIEW_FILE ? (
                                                        <CircularProgress size={16} />
                                                    ) : (
                                                        <VisibilityOutlined fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title={translate('download')}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={busy}
                                                    onClick={() =>
                                                        handleAction(ACTIONS.DOWNLOAD, retainer)
                                                    }
                                                    aria-label={translate('download')}
                                                >
                                                    {pendingAction === ACTIONS.DOWNLOAD ? (
                                                        <CircularProgress size={16} />
                                                    ) : (
                                                        <DownloadOutlined fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        {/* Emails the signing link — also the only way the
                                            server-generated visa retainer reaches the client */}
                                        {status !== 'signed' ? (
                                            <Tooltip title={sendLabel}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        disabled={busy}
                                                        onClick={() =>
                                                            handleAction(ACTIONS.SEND, retainer)
                                                        }
                                                        aria-label={sendLabel}
                                                    >
                                                        {pendingAction === ACTIONS.SEND ? (
                                                            <CircularProgress size={16} />
                                                        ) : (
                                                            <SendOutlined fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        ) : null}
                                        {status !== 'signed' ? (
                                            <Tooltip title={signingActionLabel}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color={
                                                            signingBlocked ? 'success' : 'warning'
                                                        }
                                                        disabled={busy}
                                                        onClick={() =>
                                                            handleAction(ACTIONS.EDIT, retainer)
                                                        }
                                                        aria-label={signingActionLabel}
                                                    >
                                                        {pendingAction === ACTIONS.EDIT ? (
                                                            <CircularProgress size={16} />
                                                        ) : signingBlocked ? (
                                                            <LockOpenOutlined fontSize="small" />
                                                        ) : (
                                                            <LockOutlined fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        ) : null}
                                        <Tooltip title={translate('delete')}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    disabled={busy}
                                                    onClick={() =>
                                                        handleAction(ACTIONS.DELETE, retainer)
                                                    }
                                                    aria-label={translate('delete')}
                                                >
                                                    <DeleteOutlined fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};
